import { useCallback, useEffect, useRef, useState } from 'react';
import { ProfilActions } from './Profil.actions';
import { NotifyType } from '../../components/Notification/Notification.types';
import { useSessionStore } from '../../lib/Store/sessionStore.component';
import { AgentApi } from '../../api/Agent';
import { DocumentApi } from '../../api/Document';
import { downloadFile } from '../../lib/generique';
import dayjs from 'dayjs';

export interface AgentInfo {
  image: string;
  abonnementMail: boolean;
  identifiantEntite: number;
  entiteLibelle: string;
  email: string;
  email2: string;
  nomClassement: string;
}

export interface Entite {
  value: string;
  label: string;
}

export const useProfilState = (notify?: NotifyType) => {
  const {getCurrentInfos, updateCurrentLoginInfo} = useSessionStore()
  const {currentFiche} = getCurrentInfos()

  const [dialogVisible, setDialogVisible] = useState(false);
  const [isModalVisible, setModalVisible] = useState(false);
  const [email, setEmail] = useState((currentFiche?.loginInfo.emailPersonnel && currentFiche?.loginInfo.emailPersonnel !== "") 
    ? currentFiche?.loginInfo.emailPersonnel
    : currentFiche?.loginInfo.emailProfessionnel || ""
  )

  const toggleSwitch = useCallback(async () => {
    const abonnementMail = currentFiche?.loginInfo.courrielEspace === 1 ? 0 : 1
    try {
      const { currentLogin } = useSessionStore.getState().getCurrentInfos();
      if (!currentLogin?.identifiant) {
        throw new Error('Session utilisateur non trouvée');
      }
      
      // Mise à jour via l'API Agent
      await AgentApi.updateAgent(currentLogin.identifiant, {
        courrielEspace: abonnementMail
      });

      // Mettre à jour les loginInfo dans le sessionStore
      useSessionStore.getState().updateCurrentLoginInfo({
        courrielEspace: abonnementMail
      });
      
      notify?.success('Préférences email mises à jour avec succès');
      
    } catch (error) {     
      notify?.error('Erreur lors de la mise à jour des préférences email');
      throw error; // Re-throw pour que le composant puisse gérer l'erreur
    }
  }, [currentFiche?.loginInfo.courrielEspace, notify]);

  const toggleDialog = () => {
    setDialogVisible(!dialogVisible);
  };

  const changeMail = (value: string) => {
    setEmail(value)
  };

  // Fonction pour sauvegarder l'email (appelée quand l'utilisateur termine la saisie)
  const saveEmail = async () => {
    try {
      if (currentFiche?.loginInfo.emailPersonnel) {
        // C'est l'email personnel
        await ProfilActions.updateEmail(email, 'personnel', notify);
      } else {
        // C'est l'email professionnel
        await ProfilActions.updateEmail(email, 'professionnel', notify);
      }
    } catch (error) {
      notify?.error(`Erreur lors de la mise à jour de l'email ${email}`);       
    }
  };

  const handleSelectEntite = async (item: Entite) => {
    const newIdentifiantEntite = item.value as unknown as number;
    updateCurrentLoginInfo({identifiantEntiteDefautEsag: newIdentifiantEntite})
    setModalVisible(false);

    try {
      await ProfilActions.updateEntiteDefaut(newIdentifiantEntite, notify);
    } catch (error) {
      notify?.error(`Erreur lors de la mise à jour de l'entité ${item.label}`);  
    }
  };

  useEffect(() => {
    // Charger la photo depuis l'API si absente ou URI locale stale (content://, file://)
    const uri = currentFiche?.loginInfo.imageProfil;
    const isStable = uri && (uri.startsWith('data:') || uri.startsWith('http://') || uri.startsWith('https://'));
    if (!isStable) {
      ProfilActions.getPhoto().then((imageData) => {
        updateCurrentLoginInfo({ imageProfil: imageData ?? null });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Intentionnellement vide : charger une seule fois à l'ouverture du Profil

  // ── IBAN / Signature ─────────────────────────────────────────────────────────
  // Les data URIs volumineuses ne sont PAS stockées dans le sessionStore Zustand
  // (re-renders en cascade) : état local uniquement.
  const [signatureUri, setSignatureUri] = useState<string | null>(null);
  const [ribUri, setRibUri] = useState<string | null>(null);
  const [ribUploadDate, setRibUploadDate] = useState<string | null>(null);
  const [signatureUploadDate, setSignatureUploadDate] = useState<string | null>(null);
  const [selectedRibDocument, setSelectedRibDocument] = useState<any | null>(null);
  const [selectedSignatureDocument, setSelectedSignatureDocument] = useState<any | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const result = await ProfilActions.loadAgentDocumentsAndIban();
        if (!mounted) return;
        if (result?.signature) setSignatureUri(result.signature);
        if (result?.iban) setRibUri(result.iban);
        setRibUploadDate(dayjs().format('DD/MM/YYYY'));
        setSignatureUploadDate(dayjs().format('DD/MM/YYYY'));
      } catch (err) {
        console.warn('Erreur chargement documents/IBAN:', err);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleRibFilePicked = async (result: any) => {
    if (result?.uri && result?.name) {
      const documentData = {
        file: { uri: result.uri, name: result.name, type: result.type || 'application/octet-stream' },
        filename: result.name,
        mimetype: result.type || 'application/octet-stream',
        size: result.size || 0,
      };
      setSelectedRibDocument(documentData);
      try {
        await ProfilActions.uploadRibFile(documentData.file, notify);
        const uri = await ProfilActions.fetchDocumentByType('RIB');
        if (uri) { setRibUri(uri); setRibUploadDate(dayjs().format('DD/MM/YYYY')); }
      } catch (_) {}
    }
  };

  const handleRibRemove = async () => {
    try {
      await ProfilActions.deleteRibDocument(notify);
      setSelectedRibDocument(null);
      setRibUri(null);
      setRibUploadDate(null);
    } catch (_) {}
  };

  const handleRibDownload = async () => {
    if (!ribUri) return;
    try {
      const ext = ribUri.includes('application/pdf') ? 'pdf' : (ribUri.includes('image/png') ? 'png' : 'jpg');
      const blob = await (await fetch(ribUri)).blob();
      await downloadFile(blob, `iban.${ext}`, '');
      notify?.success('Document téléchargé avec succès');
    } catch (_) {
      notify?.error('Impossible d\'ouvrir le document');
    }
  };

  const handleSignatureFilePicked = async (result: any) => {
    if (result?.uri && result?.name) {
      const documentData = {
        file: { uri: result.uri, name: result.name, type: result.type || 'application/octet-stream' },
        filename: result.name,
        mimetype: result.type || 'application/octet-stream',
        size: result.size || 0,
      };
      setSelectedSignatureDocument(documentData);
      try {
        await ProfilActions.uploadSignatureImage(documentData.file, notify);
        const uri = await ProfilActions.fetchDocumentByType('Signature');
        if (uri) { setSignatureUri(uri); setSignatureUploadDate(dayjs().format('DD/MM/YYYY')); }
      } catch (_) {}
    }
  };

  const handleSignatureRemove = async () => {
    try {
      const id = await ProfilActions.getIdentifiantDocumentByType('Signature');
      if (id) await DocumentApi.deleteDocument(id);
      setSelectedSignatureDocument(null);
      setSignatureUri(null);
      setSignatureUploadDate(null);
    } catch (e) {
      console.warn('Erreur suppression signature:', e);
    }
  };

  const handleSignatureDownload = async () => {
    if (!signatureUri) return;
    try {
      const blob = await (await fetch(signatureUri)).blob();
      await downloadFile(blob, 'signature.png', '');
      notify?.success('Document téléchargé avec succès');
    } catch (_) {
      notify?.error('Impossible d\'ouvrir la signature');
    }
  };

  return {
    // States
    dialogVisible,
    isModalVisible,
    email,

    // States IBAN / Signature
    signatureUri,
    setSignatureUri,
    ribUri,
    setRibUri,
    ribUploadDate,
    setRibUploadDate,
    signatureUploadDate,
    setSignatureUploadDate,
    selectedRibDocument,
    setSelectedRibDocument,
    selectedSignatureDocument,
    setSelectedSignatureDocument,

    // Setters
    setDialogVisible,
    setModalVisible,

    // Methods
    toggleSwitch,
    toggleDialog,
    changeMail,
    saveEmail,
    handleSelectEntite,

    // Handlers IBAN / Signature
    handleRibFilePicked,
    handleRibRemove,
    handleRibDownload,
    handleSignatureFilePicked,
    handleSignatureRemove,
    handleSignatureDownload,
  };
};